"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from '@/shared/ui/icons';

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";
import { SURFACE_CLOSE_TARGET } from './surface-controls';
import { useShellInspector } from "../../hooks/useShellInspector";

const RadixSheet = SheetPrimitive as any;
const hasRadixSheet =
  typeof (RadixSheet as any)?.Root !== "undefined" &&
  typeof (RadixSheet as any)?.Trigger !== "undefined" &&
  typeof (RadixSheet as any)?.Content !== "undefined";

let hasWarnedMissingRadixSheet = false;
const warnMissingRadixSheet = () => {
  if (hasWarnedMissingRadixSheet) return;
  hasWarnedMissingRadixSheet = true;
  console.warn(
    "[ui/sheet] Radix Dialog primitives unavailable. Falling back to basic markup.",
  );
};

function Sheet({ children, ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  if (!hasRadixSheet) {
    warnMissingRadixSheet();
    const isOpen = (props as any)?.open;
    if (isOpen === false) return null;
    return (
      <div data-slot="sheet-fallback" {...(props as any)}>
        {children}
      </div>
    );
  }
  return (
    <RadixSheet.Root data-slot="sheet" {...props}>
      {children}
    </RadixSheet.Root>
  );
}

/**
 * Contextual inspectors claim the app-shell right panel on desktop, matching
 * New Project. Outside the shell or on mobile, retain the non-modal sheet.
 * Pair with InspectorPanel; ordinary modal sheets and action drawers are separate.
 */
const InspectorShellContext = React.createContext<{
  host: HTMLElement | null;
  active: boolean;
  open: boolean;
  titleId?: string;
}>({ host: null, active: false, open: false });

function InspectorSheet({ open: controlledOpen, defaultOpen = false, active: featureActive = true, onOpenChange, children, ...props }: React.ComponentProps<typeof SheetPrimitive.Root> & { active?: boolean }) {
  const titleId = React.useId();
  const [localOpen, setLocalOpen] = React.useState(defaultOpen);
  const open = controlledOpen ?? localOpen;
  const changeOpen = (next: boolean) => {
    if (controlledOpen === undefined) setLocalOpen(next);
    onOpenChange?.(next);
  };
  const { host, active } = useShellInspector(open && featureActive, () => changeOpen(false));
  return (
    <InspectorShellContext.Provider value={{ host, active, open, titleId }}>
      <Sheet {...props} open={open && featureActive} onOpenChange={changeOpen} modal={false}>{children}</Sheet>
    </InspectorShellContext.Provider>
  );
}

const SheetTrigger = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Trigger>,
  React.ComponentProps<typeof SheetPrimitive.Trigger>
>(({ ...props }, ref) => (
  hasRadixSheet && typeof (RadixSheet as any)?.Trigger !== "undefined" ? (
    <RadixSheet.Trigger ref={ref} data-slot="sheet-trigger" {...props} />
  ) : (
    <button ref={ref as any} data-slot="sheet-trigger-fallback" {...(props as any)} />
  )
));

SheetTrigger.displayName = "SheetTrigger";

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Close === "undefined") {
    warnMissingRadixSheet();
    return <button data-slot="sheet-close-fallback" {...(props as any)} />;
  }
  return <RadixSheet.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Portal === "undefined") {
    warnMissingRadixSheet();
    return (
      <div data-slot="sheet-portal-fallback" {...(props as any)}>
        {children}
      </div>
    );
  }
  // children (the overlay + content) must be passed through — without them the
  // portal mounts empty and every Sheet renders nothing, with no error.
  return (
    <RadixSheet.Portal data-slot="sheet-portal" {...props}>
      {children}
    </RadixSheet.Portal>
  );
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentProps<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Overlay === "undefined") {
    warnMissingRadixSheet();
    return null;
  }
  return (
    <RadixSheet.Overlay
      ref={ref}
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100] bg-black/50",
        className,
      )}
      {...props}
    />
  );
});

SheetOverlay.displayName = "SheetOverlay";

function SheetContent({
  className,
  children,
  side = "right",
  hideOverlay = false,
  hideCloseButton = false,
  portalContainer,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  hideOverlay?: boolean;
  hideCloseButton?: boolean;
  portalContainer?: HTMLElement | null;
} & NativeMetadataProps) {
  const { nativeProps, rest } = splitNativeProps(props);
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Content === "undefined") {
    warnMissingRadixSheet();
    return (
      <>
        {!hideOverlay && (
          <div
            data-slot="sheet-overlay-fallback"
            className="fixed inset-0 z-[100] bg-black/50"
          />
        )}
        <div
          data-slot="sheet-content-fallback"
          data-native-role={nativeProps["data-native-role"] ?? "sheet"}
          data-native-presentation={nativeProps["data-native-presentation"] ?? "sheet"}
          className={cn(
            "bg-background fixed z-[110] flex flex-col gap-4 glass-shadow-strong",
            side === "right" && "inset-y-0 right-0 h-full w-[95%] border-l sm:max-w-[800px]",
            side === "left" && "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
            side === "top" && "inset-x-0 top-0 h-auto border-b",
            side === "bottom" && "inset-x-0 bottom-0 h-auto border-t",
            className,
          )}
          {...nativeProps}
          {...(rest as any)}
        >
          {children}
          {!hideCloseButton && (
            <button
              data-slot="sheet-close-fallback"
              className={cn(SURFACE_CLOSE_TARGET, 'ring-offset-background focus:ring-ring absolute rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden')}
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </button>
          )}
        </div>
      </>
    );
  }
  return (
    <SheetPortal container={portalContainer}>
      {!hideOverlay && <SheetOverlay />}
      <RadixSheet.Content
        data-slot="sheet-content"
        data-native-role={nativeProps["data-native-role"] ?? "sheet"}
        data-native-presentation={nativeProps["data-native-presentation"] ?? "sheet"}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-[110] flex flex-col gap-4 glass-shadow-strong transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[95%] border-l sm:max-w-[800px]",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className,
        )}
        {...nativeProps}
        {...rest}
      >
        {children}
        {!hideCloseButton && (
          <RadixSheet.Close className={cn(SURFACE_CLOSE_TARGET, 'ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none')}>
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </RadixSheet.Close>
        )}
      </RadixSheet.Content>
    </SheetPortal>
  );
}

/**
 * Standard right-edge inspector surface. This deliberately owns the
 * non-blocking presentation defaults so feature panels cannot accidentally
 * drift back to modal drawer chrome.
 */
function InspectorPanel({
  className,
  onInteractOutside,
  style,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onPointerDownOutside,
  onFocusOutside,
  portalContainer,
  hideCloseButton,
  children,
  ...props
}: Omit<React.ComponentProps<typeof SheetContent>, "side" | "hideOverlay">) {
  const { host, active, open, titleId } = React.useContext(InspectorShellContext);
  if (host) {
    if (!open) return null;
    // The shell owns focus and Escape. A hidden Radix DismissableLayer would
    // consume Escape intended for Notifications or a nested editor.
    return createPortal(
      <div
        {...props}
        role="dialog"
        aria-modal={false}
        aria-labelledby={titleId}
        data-slot="sheet-content"
        data-native-role="inspector"
        data-native-presentation="inspector"
        data-quantis-inspector="true"
        data-shell-inspector="true"
        className={cn("bg-background flex flex-col gap-4 font-apple", className)}
        style={{
          ...style,
          position: 'relative', inset: 'auto', width: '100%', maxWidth: 'none', height: '100%',
          border: 0, boxShadow: 'none', transform: 'none', animation: 'none',
          display: active ? 'flex' : 'none',
        }}
      >
        {children}
        {!hideCloseButton && (
          <SheetClose className={cn(SURFACE_CLOSE_TARGET, 'absolute rounded-xs opacity-70 hover:opacity-100')}>
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetClose>
        )}
      </div>,
      host,
    );
  }
  return (
    <SheetContent
      {...props}
      hideCloseButton={hideCloseButton}
      onCloseAutoFocus={onCloseAutoFocus}
      onPointerDownOutside={onPointerDownOutside}
      onFocusOutside={onFocusOutside}
      side="right"
      hideOverlay
      data-native-role="inspector"
      data-native-presentation="inspector"
      data-quantis-inspector="true"
      portalContainer={portalContainer}
      className={cn("font-apple", className)}
      style={style}
      onEscapeKeyDown={onEscapeKeyDown}
      onOpenAutoFocus={onOpenAutoFocus}
      onInteractOutside={(event) => {
        onInteractOutside?.(event);
        if (!event.defaultPrevented) event.preventDefault();
      }}
    >
      {children}
    </SheetContent>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  const { host, titleId } = React.useContext(InspectorShellContext);
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Title === "undefined") {
    warnMissingRadixSheet();
    return (
      <div
        data-slot="sheet-title-fallback"
        className={cn("text-foreground text-lg font-semibold", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixSheet.Title
      data-slot="sheet-title"
      className={cn("text-foreground text-lg font-semibold", className)}
      {...props}
      {...(host ? { id: titleId } : {})}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  if (!hasRadixSheet || typeof (RadixSheet as any)?.Description === "undefined") {
    warnMissingRadixSheet();
    return (
      <div
        data-slot="sheet-description-fallback"
        className={cn("text-muted-foreground text-sm", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixSheet.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  InspectorSheet,
  InspectorPanel,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
